import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  CircularProgress,
  Alert,
  Fade,
  Button,
  Avatar,
} from '@mui/material';
import {
  Mic,
  Send,
  SmartToy,
} from '@mui/icons-material';
import { useSettings } from '@/context/SettingsContext';
import { useWebSocket } from '@/context/WebSocketContext';
import { Message } from '@/types/chat';
import MessageList from './MessageList';
import ResultsDialog from './ResultsDialog';
import { audioService } from '@/services/audioService';

interface ChatInterfaceProps {
  onComplete?: () => void;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({ onComplete }) => {
  const { language } = useSettings();
  const ws = useWebSocket();

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isAgentThinking, setIsAgentThinking] = useState(true); // Start as true for first question
  const [isSurveyComplete, setIsSurveyComplete] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const translations = {
    en: {
      inputPlaceholder: 'Type your response or use voice...',
      connecting: 'Connecting...',
      errorTitle: 'Connection Error',
      completed: 'Survey completed! Thank you for your time.',
      viewResults: 'View Results',
    },
    ar: {
      inputPlaceholder: 'اكتب ردك أو استخدم الصوت...',
      connecting: 'جاري الاتصال...',
      errorTitle: 'خطأ في الاتصال',
      completed: 'تم الاستبيان! شكراً لوقتك.',
      viewResults: 'عرض النتائج',
    },
  };

  const t = translations[language];

  // Handle errors from backend
  const handleError = useCallback((data: any) => {
    console.error('❌ Backend error:', data);
    setIsAgentThinking(false);

    // Optionally show error message to user
    const errorMessage: Message = {
      id: `msg-error-${Date.now()}`,
      sender: 'agent',
      type: 'text',
      content: {
        text: data.message || 'An error occurred. Please try again.',
        language,
      },
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, errorMessage]);
  }, [language]);

  // WebSocket message handlers
  const handleAgentResponse = useCallback((data: any) => {
    setIsAgentThinking(false);

    // Handle timeout message with language-specific text
    let messageText = data.text;
    if (data.text_ar && language === 'ar') {
      messageText = data.text_ar;
    } else if (data.text) {
      messageText = data.text;
    }

    const message: Message = {
      id: `msg-${Date.now()}`,
      sender: 'agent',
      type: data.component ? 'interactive' : 'text',
      content: {
        text: messageText,
        language: data.language || language,
        component: data.component,
      },
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, message]);

    // Mark survey complete when backend says so (only sent after all questions answered)
    if (data.survey_complete) {
      setIsSurveyComplete(true);
    }
  }, [language]);

  const handleSessionComplete = useCallback(() => {
    // Mark survey as complete
    setIsSurveyComplete(true);

    // Call the onComplete callback if provided
    if (onComplete) {
      setTimeout(onComplete, 3000);
    }
  }, [onComplete]);

  // Set up WebSocket message handlers
  useEffect(() => {
    ws.on('agent_response', handleAgentResponse);
    ws.on('session_complete', handleSessionComplete);
    ws.on('error', handleError);

    return () => {
      ws.off('agent_response');
      ws.off('session_complete');
      ws.off('error');

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, [ws, handleAgentResponse, handleSessionComplete, handleError]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isAgentThinking]);

  const sendTextMessage = () => {
    if (!inputText.trim() || !ws.isConnected) return;

    // Stop any playing audio
    audioService.stop();

    // Resume audio on user interaction
    ws.resumeAudio();

    const message: Message = {
      id: `msg-user-${Date.now()}`,
      sender: 'user',
      type: 'text',
      content: {
        text: inputText,
        language,
      },
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, message]);
    setIsAgentThinking(true); // Show thinking indicator immediately
    ws.sendText(inputText, language);
    setInputText('');
  };

  const startRecording = async () => {
    // Stop any playing audio
    audioService.stop();

    // Resume audio on user interaction
    ws.resumeAudio();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Check supported MIME types
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/mp4';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = ''; // Use default
          }
        }
      }

      console.log('🎤 Using MIME type:', mimeType || 'default');

      const mediaRecorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: mimeType || 'audio/webm',
        });

        console.log('🎤 Recording stopped, blob size:', audioBlob.size, 'bytes');

        // Check if blob is valid
        if (audioBlob.size === 0) {
          console.error('❌ Empty audio blob!');
          setIsAgentThinking(false);
          return;
        }

        // Send audio to server
        if (ws.isConnected) {
          // Add user message with audio blob for playback
          const message: Message = {
            id: `msg-user-voice-${Date.now()}`,
            sender: 'user',
            type: 'voice',
            content: {
              text: '', // No text placeholder
              language,
              audioBlob, // Store the audio blob for playback
            },
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, message]);
          setIsAgentThinking(true); // Show thinking indicator immediately
          ws.sendAudio(audioBlob);
          console.log('📤 Audio sent to server');
        }

        // Stop all tracks
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      console.log('🎤 Recording started');
    } catch (err) {
      console.error('❌ Error starting recording:', err);
      alert(`Failed to start recording: ${err instanceof Error ? err.message : 'Unknown error'}\n\nPlease allow microphone access.`);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleMicClick = () => {
    if (!ws.isConnected || isAgentThinking) return;

    if (isRecording) {
      // Stop and send
      stopRecording();
    } else {
      // Start recording
      startRecording();
    }
  };

  const handleInteractionResponse = (value: any) => {
    if (!ws.isConnected) return;

    // Stop any playing audio
    audioService.stop();

    // Resume audio on user interaction
    ws.resumeAudio();

    const message: Message = {
      id: `msg-user-interaction-${Date.now()}`,
      sender: 'user',
      type: 'text',
      content: {
        text: String(value),
        language,
      },
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, message]);
    setIsAgentThinking(true); // Show thinking indicator immediately
    ws.sendInteractionResponse(value, language);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        gap: 2,
      }}
    >
      {/* Error Alert */}
      {ws.error && (
        <Fade in>
          <Alert severity="error">
            <strong>{t.errorTitle}:</strong> {ws.error}
          </Alert>
        </Fade>
      )}

      {/* Messages Area */}
      <Paper
        elevation={2}
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!ws.isConnected ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              {t.connecting}
            </Typography>
          </Box>
        ) : (
          <>
            <MessageList messages={messages} onInteractionResponse={handleInteractionResponse} />
            {isAgentThinking && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                mt: 2,
                mb: 1,
              }}>
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: 40,
                    height: 40,
                  }}
                >
                  <SmartToy />
                </Avatar>
                <Box sx={{
                  display: 'flex',
                  gap: 0.5,
                  alignItems: 'center',
                  '& .dot': {
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'text.secondary',
                    animation: 'wave 1.4s ease-in-out infinite',
                    '&:nth-of-type(1)': { animationDelay: '0s' },
                    '&:nth-of-type(2)': { animationDelay: '0.2s' },
                    '&:nth-of-type(3)': { animationDelay: '0.4s' },
                  },
                  '@keyframes wave': {
                    '0%, 60%, 100%': {
                      transform: 'translateY(0)',
                      opacity: 0.4,
                    },
                    '30%': {
                      transform: 'translateY(-10px)',
                      opacity: 1,
                    },
                  },
                }}>
                  <Box className="dot" />
                  <Box className="dot" />
                  <Box className="dot" />
                </Box>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </Paper>

      {/* View Results Button */}
      {isSurveyComplete && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            p: 2,
          }}
        >
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => setShowResults(true)}
            sx={{ minWidth: 200 }}
          >
            {t.viewResults}
          </Button>
        </Box>
      )}

      {/* Input Area - Hide when survey is complete */}
      {!isSurveyComplete && (
        <Box sx={{ position: 'relative' }}>
          <Paper
            elevation={2}
            sx={{
              p: 2,
              display: 'flex',
              gap: 1,
              alignItems: 'center',
            }}
          >
            {/* Voice Button - Click to Record, Click Again to Send */}
            <IconButton
              color={isRecording ? 'error' : 'primary'}
              onClick={handleMicClick}
              disabled={!ws.isConnected || isAgentThinking}
              size="large"
              sx={{
                animation: isRecording ? 'pulse 1.5s ease-in-out infinite' : 'none',
                '@keyframes pulse': {
                  '0%': {
                    boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)',
                  },
                  '70%': {
                    boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)',
                  },
                  '100%': {
                    boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)',
                  },
                },
              }}
            >
              <Mic />
            </IconButton>

            {/* Text Input */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder={t.inputPlaceholder}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onFocus={() => {
              audioService.stop();
              ws.resumeAudio();
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendTextMessage();
              }
            }}
            disabled={!ws.isConnected || isAgentThinking || isRecording}
            multiline
            maxRows={3}
          />

            {/* Send Button */}
            <IconButton
              color="primary"
              onClick={sendTextMessage}
              disabled={!ws.isConnected || !inputText.trim() || isAgentThinking}
              size="large"
            >
              <Send />
            </IconButton>
          </Paper>

          {/* Recording Indicator */}
          {isRecording && (
            <Typography
              variant="caption"
              color="error"
              sx={{
                position: 'absolute',
                bottom: -20,
                left: '50%',
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
                fontWeight: 'bold',
              }}
            >
              {language === 'en' ? 'Recording... Click mic to send' : 'جاري التسجيل... انقر للإرسال'}
            </Typography>
          )}
        </Box>
      )}

      {/* Results Dialog */}
      <ResultsDialog
        open={showResults}
        onClose={() => setShowResults(false)}
        sessionId={ws.sessionId}
      />
    </Box>
  );
};

export default ChatInterface;
